import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import ProductAddToCartButton, { InfoIconTooltipStyles } from "@tCentro/Overrides/Components/ProductAddToCartButton";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: ApplicationState, ownProps: HasProduct) => ({
    configurationSelection: state.pages.productDetails.configurationSelection,
    configurationCompleted: state.pages.productDetails.configurationCompleted,
    variantSelectionCompleted: state.pages.productDetails.variantSelectionCompleted,
    subscription: state.pages.productDetails.subscription,
});

export interface NISProductDetailsAddToCartButtonStyles {
    button?: ButtonPresentationProps & InfoIconTooltipStyles;
}

export const nisAddToCartButtonStyles: NISProductDetailsAddToCartButtonStyles = {
    button: {
        fastGuestCheckoutTextSyltes: {
            css: css`
                text-transform: capitalize !important;
                font-weight: 500 !important;
                font-size: 14px !important;
                line-height: 14px !important;
                font-style: italic;
            `,
        },
        css: css`
            width: 100%;
            border-radius: 4px !important;
            width: 200px !important;
            height: 48px !important;
            padding: 12px 16px !important;
            white-space: nowrap;
            display: flex;
            flex-direction: column;
            justify-content: center;
            margin-top: -8px !important;
            &:disabled {
                background: ${getColor("secondary")};
                border-color: ${getColor("secondary")};
                opacity: 0.5;
            }
            > span:first-of-type {
                font-size: ${TYPOGRAPHY.size.large};
                font-family: ${TYPOGRAPHY.fontFamily.base};
                line-height: 18px;
                text-align: center;
                font-weight: 700 !important;
                font-family: Roboto Condensed;
                ${({ theme }: { theme: BaseTheme }) => breakpointMediaQueries(theme, [null, null, null, null, null])}
            }
            span {
                font-family: Roboto Condensed !important;
                text-transform: uppercase;
                align-self: center;
            }
            > span:second-of-type {
                text-transform: capitalize !important;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: max-content !important;
                        `,
                        css`
                            margin-top: 16px;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
            @media (min-width: 992px) and (max-width: 1180px) {
                width: 240px !important;
            }
            @media (max-width: 991px) {
                width: 220px !important;
            }

            @media (min-width: 768px) and (max-width: 1280px) {
                width: 200px !important;
            }
            @media (max-width: 767px) {
                margin-top: 0px;
            }
            @media (max-width: 575px) {
                width: 100% !important;
            }
        `,

        hiddenMobileToolTip: {
            css: css`
                display: inline;
                position: relative;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            left: 128px;
                            top: 5px;
                            position: absolute;
                        `,
                        css`
                            left: 128px;
                            top: 5px;
                            position: absolute;
                        `,
                        css`
                            left: 14%;
                            top: 5px;
                            position: absolute;
                        `,
                        css`
                            @media (min-width: 992px) and (max-width: 1024px) {
                                top: 5px;
                                left: 15%;
                                position: absolute;
                            }
                        `,
                        null,
                    ])}
                @media (max-width: 420px) {
                    left: 110px;
                    top: 5px;
                }
            `,
        },
        mobileTooltip: {
            cssOverrides: {
                tooltipBody: css`
                    left: 0px;
                `,
            },
        },
        tooltipButtonContainer: {
            css: css`
                display: flex;
                position: relative;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            flex-direction: column-reverse;
                            align-items: flex-start !important;
                            margin-top: 0px;
                        `,
                        css`
                            flex-direction: column-reverse;
                            align-items: flex-start !important;
                            margin-top: 0px;
                        `,
                        css`
                            flex-direction: column-reverse;
                            align-items: flex-start !important;
                        `,
                        null,
                        null,
                    ])}
            `,
        },
    },
};

const styles = nisAddToCartButtonStyles;

const NISProductDetailsAddToCartButton = ({
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    subscription,
}: Props) => {
    return (
        <ProductAddToCartButton
            configurationSelection={configurationSelection}
            configurationCompleted={configurationCompleted}
            variantSelectionCompleted={variantSelectionCompleted}
            subscription={subscription}
            extendedStyles={styles.button}
            data-test-selector="addProductToCart"
        />
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(mapStateToProps)(NISProductDetailsAddToCartButton)),
    definition: {
        displayName: "NIS Add to Cart Button",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
